import type { CodigoAviso } from './sesionMachine';

/**
 * Textos de la sesión (D-11 de la tanda 2). Siempre se resuelven por
 * `codigo`, nunca por el `mensaje` del servidor, que es orientativo y
 * puede cambiar (JC §0.5). Redacción impersonal, sin tuteo ni usted.
 * Un solo módulo, en español; no hay i18n en esta etapa.
 */

/** Qué hace la pantalla después de un `sesion.error`, según JC §0.5. */
export type AccionError =
  /** Token vencido o inválido: login nuevo. No pedir otro ticket. */
  | 'volver_a_ingresar'
  /** Pedir un ticket nuevo (Paso 3). */
  | 'reintentar'
  /** Volver a la pantalla de la simulación asignada (Paso 2). */
  | 'volver_al_inicio';

interface TextoError {
  texto: string;
  accion: AccionError;
}

const ERRORES_SESION: Record<string, TextoError> = {
  no_autenticado: {
    texto: 'La sesión expiró. Es necesario volver a ingresar.',
    accion: 'volver_a_ingresar',
  },
  ticket_invalido: {
    texto: 'No se pudo validar el acceso a la entrevista.',
    accion: 'reintentar',
  },
  simulacion_cerrada: {
    texto: 'Esta entrevista ya fue finalizada.',
    accion: 'volver_al_inicio',
  },
  reconexion_desde_otro_cliente: {
    texto: 'La entrevista se abrió en otra ventana o en otro dispositivo.',
    accion: 'volver_al_inicio',
  },
  sts_init_failed: {
    texto: 'No se pudo iniciar el audio de la entrevista.',
    accion: 'volver_al_inicio',
  },
  // Código del cliente, no del servidor: "Reintentar" tras una reconexión
  // fallida y `mi-simulacion` ya no trae una sesión en curso.
  sin_simulacion_en_curso: {
    texto: 'La entrevista ya no está en curso.',
    accion: 'volver_al_inicio',
  },
};

const ERROR_SESION_GENERICO: TextoError = {
  texto: 'Ocurrió un problema con la entrevista.',
  accion: 'volver_al_inicio',
};

/** Texto y acción para un código de `sesion.error`. Código desconocido → genérico y Paso 2. */
export function textoErrorSesion(codigo: string | null): TextoError {
  return (codigo !== null ? ERRORES_SESION[codigo] : undefined) ?? ERROR_SESION_GENERICO;
}

const AVISOS: Record<CodigoAviso, string> = {
  audio_vacio: 'No se escuchó audio. Hablar más cerca del micrófono e intentar de nuevo.',
  sin_respuesta: 'Sin respuesta del paciente. Intentar de nuevo.',
  turno_fallido: 'No se pudo procesar el turno. Intentar de nuevo.',
  audio_no_llego: 'No llegó el audio del paciente. Se puede continuar.',
  grabacion_fallida: 'No se pudo grabar. Intentar de nuevo.',
};

export function textoAviso(codigo: CodigoAviso): string {
  return AVISOS[codigo];
}

const FALLAS_INICIO: Record<string, string> = {
  microfono: 'No hay acceso al micrófono. Revisar el permiso del navegador para este sitio.',
  token: 'La sesión no es válida. Es necesario volver a ingresar.',
  iniciar: 'No se pudo iniciar la entrevista.',
  sin_simulacion: 'No hay una entrevista en curso para retomar.',
};

/** Texto para el motivo de `inicio_fallido` o `cierre_fallido`. */
export function textoFalla(motivo: string | null): string {
  return (motivo !== null ? FALLAS_INICIO[motivo] : undefined) ?? 'Ocurrió un problema.';
}

export const TEXTOS = {
  noCompatible: 'Este navegador no es compatible. Usar Chrome en un computador.',
  reconectando: 'Reconectando…',
  reconexionFallida: 'Se perdió la conexión con la entrevista.',
  cierreFallido: 'No se pudo registrar el cierre de la entrevista.',
  finalizada: 'Entrevista finalizada',
  confirmarCierreTitulo: '¿Finalizar la entrevista?',
  confirmarCierreDetalle: 'Una vez finalizada, no se puede retomar.',
  confirmarSalidaTitulo: '¿Salir?',
  confirmarSalidaDetalle: 'La entrevista queda abierta y se puede retomar al volver a ingresar.',
} as const;
